/**
 * Sizing for the move-option chips (three per row, 31% of the row each).
 * Picks a font size and line count so the whole word is visible: shrink
 * first, and fall back to two lines when shrinking alone would go too small.
 */

// Average lowercase glyph width as a fraction of font size, rounded up so
// the estimate errs towards fitting.
const CHAR_WIDTH_EM = 0.58;
const MIN_ONE_LINE_SIZE = 12;
const MIN_SIZE = 11;

// Must match AvailableWordsDisplay styles: container margin 5, max width
// 600, row padding 8, cell 31%, chip padding 6 and border 1 per side.
const CONTAINER_MARGIN = 5;
const CONTAINER_MAX = 600;
const ROW_PADDING = 8;
const CELL_FRACTION = 0.31;
const CHIP_INSET = 14;

export interface ChipLabelLayout {
  fontSize: number;
  numberOfLines: 1 | 2;
}

/** Width available to the label inside one chip for a given window width. */
export function chipTextWidth(windowWidth: number): number {
  const container = Math.min(windowWidth - 2 * CONTAINER_MARGIN, CONTAINER_MAX);
  const cell = (container - 2 * ROW_PADDING) * CELL_FRACTION;
  return Math.max(cell - CHIP_INSET, 0);
}

export function chipLabelLayout(
  word: string,
  windowWidth: number,
  baseSize: number,
): ChipLabelLayout {
  const available = chipTextWidth(windowWidth);
  const perChar = Math.max(word.length, 1) * CHAR_WIDTH_EM;

  const oneLine = Math.floor(available / perChar);
  if (oneLine >= baseSize) return { fontSize: baseSize, numberOfLines: 1 };
  if (oneLine >= MIN_ONE_LINE_SIZE) {
    return { fontSize: oneLine, numberOfLines: 1 };
  }

  const twoLines = Math.floor((2 * available) / perChar);
  return {
    fontSize: Math.max(Math.min(baseSize - 1, twoLines), MIN_SIZE),
    numberOfLines: 2,
  };
}
